import { useState } from "react";
import { submitLead } from "../services/api";

const COUNTRIES = ["UK", "USA", "Canada", "Australia", "Ireland", "Germany", "Other"];
const LOAN_AMOUNTS = [
  "Below ₹5 Lakhs",
  "₹5 Lakhs – ₹10 Lakhs",
  "₹10 Lakhs – ₹20 Lakhs",
  "₹20 Lakhs – ₹40 Lakhs",
  "Above ₹40 Lakhs",
];

const INITIAL = {
  full_name: "",
  mobile_number: "",
  email: "",
  study_country: "",
  loan_amount: "",
  consent: false,
};

function validate(fields) {
  const errors = {};
  if (!fields.full_name.trim()) errors.full_name = "Full name is required.";
  if (!fields.mobile_number.trim()) {
    errors.mobile_number = "Mobile number is required.";
  } else if (!/^[6-9]\d{9}$/.test(fields.mobile_number.trim())) {
    errors.mobile_number = "Enter a valid 10-digit Indian mobile number.";
  }
  if (!fields.email.trim()) {
    errors.email = "Email is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim())) {
    errors.email = "Enter a valid email address.";
  }
  if (!fields.study_country) errors.study_country = "Please select a country.";
  if (!fields.loan_amount)   errors.loan_amount   = "Please select a loan amount.";
  if (!fields.consent)       errors.consent       = "You must accept the terms and conditions.";
  return errors;
}

export default function LoanLeadForm() {
  const [fields, setFields]       = useState(INITIAL);
  const [errors, setErrors]       = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState(null); // { qr_code_b64, full_name, whatsapp_sent }

  function handleChange(e) {
    let { name, value, type, checked } = e.target;
    if (name === "mobile_number") {
      // Strip non-digits, then strip leading country code (91 or 0) if present
      value = value.replace(/\D/g, "");
      if (value.startsWith("91") && value.length > 10) value = value.slice(2);
      if (value.startsWith("0")) value = value.slice(1);
      value = value.slice(0, 10);
    }
    setFields((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name])   setErrors((prev) => ({ ...prev, [name]: "" }));
    if (serverError)    setServerError("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validationErrors = validate(fields);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);
    setServerError("");
    try {
      const data = await submitLead({
        ...fields,
        full_name:     fields.full_name.trim(),
        mobile_number: fields.mobile_number.trim(),
        email:         fields.email.trim(),
      });
      setResult(data);
    } catch (err) {
      setServerError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setFields(INITIAL);
    setErrors({});
    setServerError("");
    setResult(null);
  }

  if (result) {
    return (
      <div className="card-body">
        {/* Success banner */}
        <div className="success-banner">
          <div className="check-icon">✓</div>
          <p>
            Thank you <strong>{result.full_name}</strong>! Your education loan enquiry
            has been submitted successfully. Our team will contact you shortly.
          </p>
        </div>

        {/* QR Code */}
        {result.qr_code_b64 && (
          <div className="qr-section">
            <p className="qr-title">Your Registration QR Code</p>
            <img
              src={`data:image/png;base64,${result.qr_code_b64}`}
              alt="Registration QR Code"
              className="qr-image"
            />
            <p className="qr-hint">
              {result.whatsapp_sent
                ? "✅ QR code sent to your WhatsApp. Show it at our office for verification."
                : "📲 Save this QR code. Show it at our office for verification."}
            </p>
          </div>
        )}

        <button className="btn-new" onClick={handleReset}>
          Submit Another Enquiry
        </button>
      </div>
    );
  }

  return (
    <form className="card-body" onSubmit={handleSubmit} noValidate>
      <h1 className="form-heading">Apply Now</h1>
      <p className="form-subheading">Fill in your details and we'll get back to you.</p>

      {serverError && <div className="server-error">{serverError}</div>}

      <div className="form-group">
        <label htmlFor="full_name">Full Name</label>
        <input id="full_name" name="full_name" type="text" placeholder="Enter Your Name"
          value={fields.full_name} onChange={handleChange}
          className={errors.full_name ? "error" : ""} autoComplete="name" />
        {errors.full_name && <span className="error-text">{errors.full_name}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="mobile_number">Mobile Number</label>
        <div className={`phone-input-wrapper${errors.mobile_number ? " error" : ""}`}>
          <span className="phone-prefix">+91</span>
          <input id="mobile_number" name="mobile_number" type="tel" placeholder="Enter 10-digit number"
            value={fields.mobile_number} onChange={handleChange}
            maxLength={10} inputMode="numeric" autoComplete="tel" />
        </div>
        {errors.mobile_number && <span className="error-text">{errors.mobile_number}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input id="email" name="email" type="email" placeholder="Enter Your Email"
          value={fields.email} onChange={handleChange}
          className={errors.email ? "error" : ""} autoComplete="email" />
        {errors.email && <span className="error-text">{errors.email}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="study_country">Country Planning to Study In</label>
        <div className="select-wrapper">
          <select id="study_country" name="study_country" value={fields.study_country}
            onChange={handleChange} className={errors.study_country ? "error" : ""}>
            <option value="">Select Country</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="select-arrow">▾</span>
        </div>
        {errors.study_country && <span className="error-text">{errors.study_country}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="loan_amount">Loan Amount</label>
        <div className="select-wrapper">
          <select id="loan_amount" name="loan_amount" value={fields.loan_amount}
            onChange={handleChange} className={errors.loan_amount ? "error" : ""}>
            <option value="">Select Loan Amount</option>
            {LOAN_AMOUNTS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <span className="select-arrow">▾</span>
        </div>
        {errors.loan_amount && <span className="error-text">{errors.loan_amount}</span>}
      </div>

      <div className="checkbox-group">
        <input id="consent" name="consent" type="checkbox"
          checked={fields.consent} onChange={handleChange} />
        <label htmlFor="consent">
          By clicking on this checkbox, I hereby agree to{" "}
          <a href="https://uniglobaleducon.com/" target="_blank" rel="noreferrer">Terms and Conditions</a>
          {" "}and{" "}
          <a href="https://uniglobaleducon.com/" target="_blank" rel="noreferrer">Privacy Policy</a>.
        </label>
      </div>
      {errors.consent && (
        <span className="error-text" style={{ marginTop: "-16px", display: "block", marginBottom: "16px" }}>
          {errors.consent}
        </span>
      )}

      <button type="submit" className="btn-submit" disabled={loading}>
        {loading ? "Submitting…" : "Apply Now"}
      </button>
    </form>
  );
}
